/**
 * Bug Report Modal Component
 * Modal dialog for submitting bug reports with screenshots
 */

import React, { useState, useRef, useEffect } from 'react';
import { useBugReport } from '../hooks/useBugReport';
import { getGameContext, getDeviceInfo, getBuildInfo } from '../utils/bugReportContext';
import './BugReportModal.css';

interface BugReportModalProps {
  onClose: () => void;
}

const BugReportModal: React.FC<BugReportModalProps> = ({ onClose }) => {
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [buildInfo, setBuildInfo] = useState<{ version: string; buildTime: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { submitBugReport, isSubmitting, error, success, reset } = useBugReport();

  // Get context for display
  const gameContext = getGameContext();
  const deviceInfo = getDeviceInfo();
  const reportTimestamp = new Date().toLocaleString();

  // Focus on textarea when modal opens
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Fetch build info when modal opens
  useEffect(() => {
    getBuildInfo().then(setBuildInfo);
  }, []);

  // Handle paste event for screenshots
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          if (blob) {
            handleScreenshotChange(blob);
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  const handleScreenshotChange = (file: File) => {
    setScreenshot(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshotPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleScreenshotChange(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleScreenshotChange(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!description.trim()) {
      return;
    }

    await submitBugReport(description, screenshot || undefined);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  // If successfully submitted, show success message
  if (success) {
    return (
      <div className="bug-report-modal-overlay" onClick={handleClose}>
        <div className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
          <div className="bug-report-success">
            <div className="success-icon">✓</div>
            <h2>Bug Report Submitted!</h2>
            <p>Thank you for helping us improve mfers arcade.</p>
            <p>Your report has been recorded and we'll look into it.</p>
            <button className="bug-report-btn primary" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bug-report-modal-overlay" onClick={handleClose}>
      <div className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bug-report-header">
          <h2>Report a Bug</h2>
          <button className="close-btn" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bug-report-form">
          <div className="form-group">
            <label htmlFor="description">
              What happened? <span className="required">*</span>
            </label>
            <textarea
              ref={textareaRef}
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the bug you encountered..."
              rows={6}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Screenshot (optional)</label>
            <div
              className="screenshot-dropzone"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              {screenshotPreview ? (
                <div className="screenshot-preview">
                  <img src={screenshotPreview} alt="Screenshot preview" />
                  <button
                    type="button"
                    className="remove-screenshot"
                    onClick={handleRemoveScreenshot}
                    disabled={isSubmitting}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="screenshot-placeholder">
                  <p>Drop an image here, paste (Ctrl/Cmd+V), or</p>
                  <button
                    type="button"
                    className="bug-report-btn secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSubmitting}
                  >
                    Choose File
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="bug-report-details">
            <button
              type="button"
              className="details-toggle"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? '▼' : '▶'} Technical Details (auto-collected)
            </button>
            {showDetails && (
              <div className="details-content">
                {buildInfo && (
                  <>
                    <div className="detail-item">
                      <strong>Version:</strong> {buildInfo.version}
                    </div>
                    <div className="detail-item">
                      <strong>Deploy Date:</strong> {new Date(buildInfo.buildTime).toLocaleString()}
                    </div>
                  </>
                )}
                <div className="detail-item">
                  <strong>Report Date:</strong> {reportTimestamp}
                </div>
                <div className="detail-item">
                  <strong>Game:</strong> {gameContext?.game || 'N/A'}
                </div>
                {gameContext?.game_uuid && (
                  <div className="detail-item">
                    <strong>Game UUID:</strong> {gameContext.game_uuid}
                  </div>
                )}
                {gameContext?.level_name && (
                  <div className="detail-item">
                    <strong>Level:</strong> {gameContext.level_name}
                  </div>
                )}
                {gameContext?.current_score !== undefined && (
                  <div className="detail-item">
                    <strong>Score:</strong> {gameContext.current_score}
                  </div>
                )}
                <div className="detail-item">
                  <strong>Browser:</strong> {deviceInfo.browser}
                </div>
                <div className="detail-item">
                  <strong>OS:</strong> {deviceInfo.os}
                </div>
                <div className="detail-item">
                  <strong>Screen:</strong> {deviceInfo.screenResolution}
                </div>
                <div className="detail-item">
                  <strong>URL:</strong> {gameContext?.url}
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="bug-report-error">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="bug-report-actions">
            <button
              type="button"
              className="bug-report-btn secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bug-report-btn primary"
              disabled={isSubmitting || !description.trim()}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BugReportModal;
